import { useEffect, useState } from "react";

import { toast } from "~/hooks/use-toast";

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  loading: boolean;
  error: string | null;
}

export const useGeolocation = (requestOnMount = false) => {
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    accuracy: null,
    loading: false,
    error: null,
  });

  const getGeolocation = () => {
    if (!navigator.geolocation) {
      setState((prev) => ({
        ...prev,
        error: "Geolocation is not supported by this browser",
      }));

      toast({
        title: "Error de ubicación",
        description: "La geolocalización no está disponible en este navegador",
        variant: "destructive",
      });

      return Promise.reject(new Error("Geolocation is not supported"));
    }

    setState((prev) => ({ ...prev, loading: true }));

    return new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          setState({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            loading: false,
            error: null,
          });
          resolve(position);
        },
        (error: GeolocationPositionError) => {
          let errorMessage = "Error desconocido al obtener la ubicación";

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage =
                "Acceso a la ubicación denegado. Por favor, conceda permisos de ubicación.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage =
                "La información de ubicación no está disponible en este momento.";
              break;
            case error.TIMEOUT:
              errorMessage =
                "Se agotó el tiempo de espera para obtener la ubicación.";
              break;
          }

          setState({
            latitude: null,
            longitude: null,
            accuracy: null,
            loading: false,
            error: errorMessage,
          });

          toast({
            title: "Error de ubicación",
            description: errorMessage,
            variant: "destructive",
          });

          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  useEffect(() => {
    if (requestOnMount) {
      getGeolocation();
    }
  }, [requestOnMount]);

  return { ...state, getGeolocation };
};
